// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  useSession,
  ChangeEmailTakenError,
  changeEmailAvailable,
} from '../session-context.js';
import { ApiError } from '../api.js';
import { SessionExpiredModal } from './session-expired-modal.js';
import { KycModal } from './kyc-modal.js';
import { BrowserWipeModal } from './browser-wipe-modal.js';
import { GeoServicingBanner } from './geo-restricted.js';
import { registerBrowserWipeMount } from '../browser-wipe-trigger.js';
import '../styles/layout.css';

interface LayoutProps {
  children: ComponentChildren;
}

/** COB-510: destination for the footer's Privacy link. The policy is
 *  published on the cobault.io home bundle, not by the SPA, so this is a
 *  cross-origin link. It follows the same runtime hostname shim the rest of
 *  the estate uses for cross-surface links (`WAITLIST_URL` in pages/home.tsx,
 *  and the inline shims in web/home + web/net): the bundle default is the
 *  PROD URL so byte-identical SPA promotion behaves correctly on prod with no
 *  per-env build, and the staging host is the runtime override. No new env var
 *  — the SPA has no `VITE_*` / `import.meta.env` flags by design (spa-config).
 *
 *  The clean `/privacy` form depends on the COB-571 viewer-request
 *  extensionless→.html rewrite in home-client{,-prod}.yaml — the object key
 *  stays privacy.html, and /privacy.html keeps serving for old links. */
/** Exported since COB-512: the shared-vault consent surfaces link the privacy
 *  policy from the accept-path disclosure block (COB-521 Do-1) and must resolve
 *  the same cross-origin host, not re-derive it. */
export const PRIVACY_URL =
  location.hostname === 'wrap.staging.cobault.io'
    ? 'https://staging.cobault.io/privacy'
    : 'https://cobault.io/privacy';

/** F17.3: returns true when the authenticated user still needs to clear
 *  the KYC gate. Exported for tests. The `kycStatus === null` case is
 *  pre-bootstrap (session-context hasn't read the row yet) — render
 *  nothing rather than flash a banner that may immediately disappear.
 *
 *  COB-570 (was COB-476, inverted): the offer-gate term is **admission, not
 *  exemption** — it no longer merely suppresses the banner for a recorded
 *  gate-out, it is what turns the banner on. Only an account the
 *  gate definitively admitted to the yield funnel (`offerGated === false` —
 *  the session's last definitive `/offer` observation said eligible) is ever
 *  prompted to verify. A definitive gate-out (`true`) and an undecided gate
 *  (`null` — a maintenance / gate-unavailable / unknown reason code, or a
 *  probe that never landed) both keep the surface dark. Identity
 *  verification exists to unlock the yield product and nothing else, so an
 *  account that cannot reach that product must not be nudged through a paid
 *  Sumsub check, and an account we cannot place must not be nudged on a
 *  guess.
 *
 *  This is the client half; `require_yield_funnel_access` in
 *  `services/user-app/src/lib.rs` refuses the same accounts at
 *  `/auth/kyc/access-token` and `/auth/kyc/status`, because hiding the
 *  banner leaves those endpoints callable. Both halves read the same gate.
 *
 *  Load-bearing constraint on the predicate: the offer gate's decision must
 *  stay independent of the caller's KYC status, or this becomes a deadlock —
 *  a whitelisted-but-unverified account has to reach verification to create
 *  its first strategy vault. Today it is (whitelist + region, decided at
 *  productco-kyc), and `create_vault`'s KYC check is the only place KYC
 *  gates anything. A gate disposition conditioned on KYC (the designed-next
 *  `kyc_required`) must land here as an ADMITTING signal, not a gate-out. */
export function kycBlocksAccess(
  isAuthenticated: boolean,
  kycStatus: string | null,
  offerGated: boolean | null,
): boolean {
  return (
    isAuthenticated &&
    kycStatus !== null &&
    kycStatus !== 'passed' &&
    offerGated === false
  );
}

/** COB-486 §5: the authenticated nav entries, in order, shared by the desktop
 *  `.header-nav` row and the mobile drawer so the two can never drift apart
 *  again (they were two hand-maintained copies). Home sits outside this list
 *  in both places — it renders whether or not the user is signed in.
 *
 *  `offerGatable` marks the entry that the per-account offer gate can grey
 *  out. Only Strategies carries it: it is the offering an ungated account
 *  cannot buy into. Shared vaults and the wallet are unaffected by the gate. */
const NAV_LINKS: ReadonlyArray<{
  href: string;
  label: string;
  offerGatable?: boolean;
}> = [
  { href: '/shared-vaults', label: 'Shared' },
  { href: '/vaults', label: 'Strategies', offerGatable: true },
  { href: '/wallet', label: 'Wallet' },
];

/** COB-486 §5: one nav entry. A gated Strategies entry renders as a `<span>`
 *  with NO href — greyed but not hidden, per the estate rule (cf.
 *  `sv-verifier-card--soon`): the offering stays visible because it is
 *  "not yet", not "never", and the thing that prevents navigation is the
 *  missing href, never the opacity. `offerGated === null` (unknown) renders a
 *  live link — never grey on an unresolved gate. */
function NavLink({
  href,
  label,
  gated,
  onNavigate,
}: {
  href: string;
  label: string;
  gated: boolean;
  onNavigate?: () => void;
}) {
  if (gated) {
    return (
      <span class="nav-link-disabled" aria-disabled="true">
        {label}
      </span>
    );
  }
  return (
    <a href={href} onClick={onNavigate}>
      {label}
    </a>
  );
}

export function Layout({ children }: LayoutProps) {
  const {
    status,
    isAuthenticated,
    idleSecondsRemaining,
    logout,
    kycStatus,
    emailVerified,
    offerGated,
    geoTier,
  } = useSession();

  // F17.1: inside the countdown threshold (≤ 60s by default) show a
  // warning chip in the header so the user has time to wiggle the
  // mouse / type something before the modal kicks them out.
  const showCountdown =
    status === 'authenticated' && typeof idleSecondsRemaining === 'number';

  // F17.3: full-access gate banner — the one consistent verification prompt
  // across the SPA, and the ONLY thing that mounts `KycModal` or calls
  // `/auth/kyc/*` (`refreshKycStatus` is used nowhere else). Browsing offers
  // stays open; accepting an offer and the wallet view are gated on the
  // underlying pages. Shown only for an account the offer gate admitted —
  // see `kycBlocksAccess`.
  const kycBlocked = kycBlocksAccess(isAuthenticated, kycStatus, offerGated);

  // COB-387: mobile nav drawer. Below the ~640px breakpoint the inline
  // `.header-nav` link row is hidden (CSS) in favour of a hamburger
  // toggle + slide-in side panel. The session-state badges are NOT part
  // of the drawer's payload — a live lock/expiry signal must stay
  // visible in the always-on bar even while the drawer is closed, so
  // `<SessionBadges>` is rendered a second time in `.header-mobile-status`,
  // a sibling that CSS shows only below the breakpoint (the desktop copy
  // inside `.header-nav` covers the >640px case, unchanged).
  const [navOpen, setNavOpen] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  // COB-500: the "Flush browser stored key material" FLOW is mounted here,
  // but its control lives at the foot of the Wallet page
  // (`pages/wallet.tsx`) — a destructive, account-scoped action belongs with
  // the other key-lifecycle controls, not in global chrome one click from
  // "Lock session".
  //
  // The flow nonetheless stays here, outside every auth gate, because its
  // last step is `logout()`: the Wallet page renders `null` as soon as
  // `isLoggedIn` goes false, so a locally-mounted modal would be destroyed
  // before its confirmation panel could report what was deleted. The Wallet
  // card reaches this mount through `browser-wipe-trigger.ts`.
  const [wipeOpen, setWipeOpen] = useState(false);

  useEffect(() => registerBrowserWipeMount(() => setWipeOpen(true)), []);

  function closeNav() {
    setNavOpen(false);
  }

  useEffect(() => {
    if (!navOpen) return;
    // Move focus into the drawer so keyboard/screen-reader users land
    // somewhere sensible, and hand it back to the toggle button once the
    // drawer closes (Esc, backdrop click, or a link navigating away).
    const previouslyFocused = document.activeElement as HTMLElement | null;
    drawerRef.current?.querySelector<HTMLElement>('a, button')?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') closeNav();
    }
    document.addEventListener('keydown', onKeyDown);
    // Prevent the page behind the drawer from scrolling while it's open.
    document.body.classList.add('nav-drawer-locked');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('nav-drawer-locked');
      (previouslyFocused ?? navToggleRef.current)?.focus();
    };
  }, [navOpen]);

  return (
    <div class="layout">
      <header class="header">
        <a href="/" class="header-brand">
          <img
            src="/cobault-mark.png"
            alt=""
            class="header-brand-mark"
            width={24}
            height={24}
          />
          <span class="header-brand-text">Cobault</span>
        </a>
        {/* Desktop inline nav — unchanged above the breakpoint. CSS hides
            this whole row below ~640px in favour of the hamburger. */}
        <nav class="header-nav">
          <a href="/">Home</a>
          {isAuthenticated &&
            NAV_LINKS.map((item) => (
              <NavLink
                key={item.href}
                href={item.href}
                label={item.label}
                gated={item.offerGatable === true && offerGated === true}
              />
            ))}
          {/* F8: session status indicator — visible when the agent
              has been unlocked at least once, so the user sees when
              the session drops to 'locked' or 'expired'. */}
          <SessionBadges
            showCountdown={showCountdown}
            idleSecondsRemaining={idleSecondsRemaining}
            status={status}
          />
          {status === 'authenticated' && (
            <button class="header-lock-btn" onClick={logout}>Lock session</button>
          )}
        </nav>
        {/* Mobile-only cluster: the compact status signal stays in the
            always-visible bar even when the drawer is shut, grouped with
            the hamburger so the pair reads as one right-hand unit (rather
            than `.header`'s space-between spreading three items apart).
            CSS hides the whole cluster above the breakpoint, where the
            desktop `.header-nav` above already covers both jobs. */}
        <div class="header-mobile-actions">
          <div class="header-mobile-status">
            <SessionBadges
              showCountdown={showCountdown}
              idleSecondsRemaining={idleSecondsRemaining}
              status={status}
            />
          </div>
          <button
            type="button"
            ref={navToggleRef}
            class="header-nav-toggle"
            aria-expanded={navOpen}
            aria-controls="mobile-nav-drawer"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setNavOpen((open) => !open)}
          >
            <span class="header-nav-toggle-bar" aria-hidden="true" />
            <span class="header-nav-toggle-bar" aria-hidden="true" />
            <span class="header-nav-toggle-bar" aria-hidden="true" />
          </button>
        </div>
      </header>
      <div
        class={`nav-drawer-backdrop${navOpen ? ' is-open' : ''}`}
        onClick={closeNav}
        aria-hidden="true"
      />
      <nav
        id="mobile-nav-drawer"
        ref={drawerRef}
        class={`nav-drawer${navOpen ? ' is-open' : ''}`}
        aria-label="Main navigation"
        aria-hidden={!navOpen}
        inert={!navOpen}
      >
        <div class="nav-drawer-head">
          <span class="nav-drawer-title">Menu</span>
          <button
            type="button"
            class="nav-drawer-close"
            onClick={closeNav}
            aria-label="Close menu"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <a href="/" onClick={closeNav}>Home</a>
        {isAuthenticated &&
          NAV_LINKS.map((item) => (
            <NavLink
              key={item.href}
              href={item.href}
              label={item.label}
              gated={item.offerGatable === true && offerGated === true}
              onNavigate={closeNav}
            />
          ))}
        {status === 'authenticated' && (
          <button
            class="header-lock-btn nav-drawer-lock-btn"
            onClick={() => {
              closeNav();
              logout();
            }}
          >
            Lock session
          </button>
        )}
      </nav>
      {/* COB-548 (GF-3): servicing-tier advisory. Above the KYC banner
          because it is the more fundamental constraint — a user who
          cannot open a vault at all has no reason to be nudged through a
          (paid) verification for one. `prohibited` never reaches here:
          SessionProvider short-circuits the whole subtree for it. */}
      {geoTier === 'servicing' && <GeoServicingBanner />}
      {kycBlocked && <KycRequiredBanner />}
      {/* COB-340: verify-email prompt. `emailVerified === false` only —
          `null` is pre-auth/unknown, and provider-verified accounts
          (social Web3Auth) never see it. */}
      {isAuthenticated && emailVerified === false && <EmailVerifyBanner />}
      <main class="content">{children}</main>
      <footer class="footer">
        {/* COB-566: credit the OPERATING entity, matching both marketing
            footers and the Platform Terms this row links to. Chain Capital
            Technologies Ltd is the UK parent and stays in the source-file
            license headers — that is code copyright, a different claim. */}
        <span class="footer-copy">© 2026 Bluemetal Services Pty Ltd</span>
        <span class="footer-sep" aria-hidden="true">·</span>
        {/* COB-397: browsewrap link — use of the platform implies acceptance
            of the Platform Terms; the page carries the full text. */}
        <a class="footer-link" href="/platform-terms">Platform Terms</a>
        <span class="footer-sep" aria-hidden="true">·</span>
        {/* COB-510: the privacy policy lives on the cobault.io home bundle,
            so this leaves the SPA origin. Opens in a new tab for the same
            reason WAITLIST_URL does — the session JWT is in-memory, so
            in-tab navigation away would sign the user out. */}
        <a
          class="footer-link"
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy
        </a>
        <span class="footer-sep" aria-hidden="true">·</span>
        {/* COB-513 (C4): the risk disclosure named as such. It is clause 8 of
            the Platform Terms, not a separate document — one text, rendered
            from `PLATFORM_TERMS_RISKS` here, on the Terms page and on both
            consent surfaces, so it cannot drift. COB-578 dropped this row
            item because the page did not exist and recorded that "each link
            returns with the page that backs it"; this is that return, and
            unlike the `href="#"` it replaces, the target resolves.
            Same-origin, so no target=_blank — the in-memory JWT is safe. */}
        <a class="footer-link" href="/platform-terms#risks">
          Risk Disclosure
        </a>
        <span class="footer-sep" aria-hidden="true">·</span>
        {/* Same row as the two marketing sites. No target=_blank:
            a mailto: does not navigate the tab, so the in-memory session JWT
            is not at risk the way it is for PRIVACY_URL. */}
        <a class="footer-link" href="mailto:support@cobault.io">
          Contact
        </a>
      </footer>
      <SessionExpiredModal />
      {wipeOpen && <BrowserWipeModal onClose={() => setWipeOpen(false)} />}
    </div>
  );
}

interface SessionBadgesProps {
  showCountdown: boolean;
  idleSecondsRemaining: number | null;
  status: string;
}

/** F8/COB-387: the session-state indicator, shared between the desktop
 *  `.header-nav` and the mobile `.header-mobile-status` strip so a
 *  locked/expiring/expired signal is never the drawer's exclusive copy —
 *  it must stay visible in whichever bar is on screen. */
function SessionBadges({
  showCountdown,
  idleSecondsRemaining,
  status,
}: SessionBadgesProps) {
  return (
    <>
      {showCountdown && (
        <span class="header-session-badge header-session-badge-warning">
          Session expires in {idleSecondsRemaining}s
        </span>
      )}
      {status === 'locked' && (
        <span class="header-session-badge header-session-badge-locked">
          Locked
        </span>
      )}
      {status === 'expired' && (
        <span class="header-session-badge header-session-badge-expired">
          Session expired
        </span>
      )}
    </>
  );
}

function EmailVerifyBanner() {
  // COB-340: slim strip prompting the user to complete the email
  // round-trip. The link lands on /verify-email (usually another tab),
  // so alongside "Resend" there's an explicit "I've clicked the link"
  // re-check instead of background polling.
  // COB-341: this also renders for returning unverified logins
  // (backfilled pre-COB-340 rows) where no email was ever sent, so the
  // copy must not assert a send; "Resend email" is their recovery path.
  // COB-346: a "Change it" affordance expands an inline form so a user
  // who typo'd their address at registration can correct it (unverified
  // accounts only — a verified email is a live security control).
  // Passwordless (web3auth/hardware) sessions only: a passcode account's
  // verifier is email-derived and the server 409-refuses it, so software
  // sessions keep the resend-only banner (`changeEmailAvailable`).
  const {
    userEmail,
    agentKind,
    resendVerificationEmail,
    refreshEmailVerification,
    changeEmail,
  } = useSession();
  const canChangeEmail = changeEmailAvailable(agentKind);
  const [busy, setBusy] = useState<'resend' | 'check' | 'save' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftEmail, setDraftEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  async function handleResend() {
    setBusy('resend');
    setNote(null);
    try {
      const status = await resendVerificationEmail();
      // 'already_verified' flips session state and unmounts the banner.
      if (status === 'ok') setNote('Sent — check your inbox.');
    } catch {
      setNote('Could not send the email. Please try again in a minute.');
    } finally {
      setBusy(null);
    }
  }

  async function handleCheck() {
    setBusy('check');
    setNote(null);
    try {
      // A verified result unmounts the banner; only the still-unverified
      // case needs a message.
      await refreshEmailVerification();
      setNote('Not verified yet — use the link in the email we sent.');
    } catch {
      setNote('Could not check just now. Please try again in a moment.');
    } finally {
      setBusy(null);
    }
  }

  function openEditor() {
    setNote(null);
    setFormError(null);
    setDraftEmail('');
    setEditing(true);
  }

  function cancelEditor() {
    setEditing(false);
    setFormError(null);
  }

  async function handleSave(event: Event) {
    event.preventDefault();
    const value = draftEmail.trim();
    if (!value) {
      setFormError('Enter an email address.');
      return;
    }
    setBusy('save');
    setFormError(null);
    try {
      await changeEmail(value);
      // Success: the banner re-renders naming the new address (still
      // unverified). If the account had verified elsewhere, `changeEmail`
      // self-heals and the banner unmounts instead.
      setEditing(false);
      setNote('Updated — check your inbox for a new link.');
    } catch (err) {
      if (err instanceof ChangeEmailTakenError) {
        setFormError(err.message);
      } else if (err instanceof ApiError) {
        // 400 (invalid / unchanged) and 429 (rate limit) carry a
        // caller-safe, specific server message — pass it through.
        setFormError(err.message);
      } else {
        setFormError('Could not update your email. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="kyc-banner" role="status">
      <div class="kyc-banner-inner">
        <div class="kyc-banner-text">
          {/* COB-543: this used to name shared-vault invitations, which no
              longer depend on a verified email — a claim link is a bearer
              credential and admits whoever opens it. The gate that DOES read
              `email_verified` is the per-user offer gate (COB-400,
              `verified_email_for_gate`): an unverified address is sent to the
              gate as null, which can only make it more restrictive, so yield
              vaults are the honest example. */}
          <strong>Verify your email address.</strong> Some features (like
          opening a yield vault) stay locked until{' '}
          {userEmail ?? 'your email'} is confirmed — use “Resend email” if
          you don't have a link.
          {canChangeEmail && (
            <>
              {' '}
              <button
                type="button"
                class="everify-banner-link"
                onClick={openEditor}
                disabled={busy !== null}
              >
                Wrong address? Change it
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          class="everify-banner-secondary"
          onClick={() => void handleCheck()}
          disabled={busy !== null}
        >
          {busy === 'check' ? 'Checking…' : "I've clicked the link"}
        </button>
        <button
          type="button"
          class="kyc-banner-cta"
          onClick={() => void handleResend()}
          disabled={busy !== null}
        >
          {busy === 'resend' ? 'Sending…' : 'Resend email'}
        </button>
      </div>
      {canChangeEmail && editing && (
        <form
          class="everify-banner-form"
          onSubmit={(e) => void handleSave(e)}
        >
          <input
            type="email"
            class="everify-banner-input"
            placeholder="you@example.com"
            aria-label="New email address"
            value={draftEmail}
            onInput={(e) =>
              setDraftEmail((e.target as HTMLInputElement).value)
            }
            disabled={busy === 'save'}
            autoFocus
          />
          <button
            type="submit"
            class="kyc-banner-cta"
            disabled={busy === 'save'}
          >
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            class="everify-banner-secondary"
            onClick={cancelEditor}
            disabled={busy === 'save'}
          >
            Cancel
          </button>
        </form>
      )}
      {formError && <p class="kyc-banner-error">{formError}</p>}
      {note && <p class="everify-banner-note">{note}</p>}
    </div>
  );
}

function KycRequiredBanner() {
  // Phase C: from `not_started` the primary CTA launches the Sumsub
  // WebSDK modal (real verification). Once an applicant has been
  // submitted (`pending`) or returned RED (`rejected`), the CTA instead
  // reconciles status against productco-kyc (poll-on-demand) so an
  // out-of-band review completion (or pending → green) is reflected here.
  const { refreshKycStatus, kycStatus } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // pending/rejected → the applicant already exists; the CTA polls.
  // not_started → the CTA opens the verification modal.
  const isReviewing = kycStatus === 'pending' || kycStatus === 'rejected';

  async function handleRefresh() {
    setError(null);
    setBusy(true);
    try {
      await refreshKycStatus();
    } catch {
      setError('Could not refresh verification status. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function handlePrimary() {
    if (isReviewing) {
      void handleRefresh();
    } else {
      setError(null);
      setModalOpen(true);
    }
  }

  // Distinct copy per non-passed status. `pending` = applicant submitted,
  // under review; `rejected` = Sumsub returned RED; otherwise not started.
  //
  // COB-504: the copy names STRATEGY vaults and nothing else. It used to say
  // verification gated "your wallet" too, which stopped being true when the
  // wallet and shared vaults were reopened pre-KYC — and a banner that
  // over-claims what it is holding back is worse than no banner: it sends
  // users to a paid check for surfaces they can already use.
  let headline = 'Complete KYC for full access.';
  let detail =
    'Browse strategies below — accepting one requires verification. Your wallet and shared vaults are already open.';
  if (kycStatus === 'pending') {
    headline = 'Your verification is in review.';
    detail =
      'We’ll unlock strategy vaults as soon as the check completes. Your wallet and shared vaults are already open.';
  } else if (kycStatus === 'rejected') {
    headline = 'Your verification was not approved.';
    detail =
      'Please contact support if you believe this is a mistake.';
  }

  return (
    <>
      <div class="kyc-banner" role="status">
        <div class="kyc-banner-inner">
          <div class="kyc-banner-text">
            <strong>{headline}</strong> {detail}
          </div>
          <button
            type="button"
            class="kyc-banner-cta"
            onClick={handlePrimary}
            disabled={busy}
          >
            {busy
              ? 'Checking…'
              : isReviewing
                ? 'Refresh status'
                : 'Complete verification'}
          </button>
        </div>
        {error && <p class="kyc-banner-error">{error}</p>}
      </div>
      {modalOpen && (
        <KycModal
          onClose={() => {
            setModalOpen(false);
            // Pick up any status change made during the session.
            void refreshKycStatus();
          }}
          onStatusMaybeChanged={() => void refreshKycStatus()}
        />
      )}
    </>
  );
}
